// src/lib/stockMode.ts
// "สต๊อกตัวเลือกย่อย: รวม / แยก" — one place that answers, for a cart line:
// WHICH row holds the stock, HOW MANY pieces are available, and HOW MANY
// pieces one sold unit consumes. Every read/deduct/restore path goes through
// here so the two modes can't drift apart.
//
//   แยก  (variantStockShared = false, the default and the old behaviour)
//     Each variant owns its stock (ProductVariant.stock). Right for a clothes
//     shop: เสื้อสีดำ and เสื้อสีขาว are different garments.
//
//   รวม  (variantStockShared = true)
//     Every variant draws from ONE pool (Product.stock). Right when the
//     variants are pack sizes of the SAME item — ครีมใบบัวบก 1/2/3 กระปุก is
//     one jar inventory, and selling the "3 กระปุก" option must take 3 jars
//     out of it. That per-unit piece count comes from unitCount, or from the
//     pack size baked into the variant name when the owner left unitCount at 1
//     (see lib/packSize.ts) — shared mode is meaningless otherwise, since one
//     pack would eat exactly one piece off the shared pool.
//
// A product with no variants is unaffected: it has always used Product.stock.

import { resolvePackSize } from './packSize'

export interface StockProduct {
  id?: string
  name?: string | null
  stock?: number | null
  unitCount?: number | null
  variantStockShared?: boolean | null
}

export interface StockVariant {
  id?: string
  name?: string | null
  stock?: number | null
  unitCount?: number | null
}

/** true = ทุกตัวเลือกใช้คลังเดียวกัน (Product.stock). */
export function isSharedStock(product: StockProduct | null | undefined): boolean {
  return product?.variantStockShared === true
}

/**
 * Where a stock move for this line must land. In shared mode the variant row
 * is bypassed entirely — its `stock` column is simply unused (the form hides
 * it), so nothing accumulates there to go stale.
 */
export function stockRowFor(
  product: StockProduct,
  variantId: string | null | undefined,
): { scope: 'variant'; variantId: string } | { scope: 'product' } {
  if (variantId && !isSharedStock(product)) return { scope: 'variant', variantId }
  return { scope: 'product' }
}

/** Pieces on hand for this line (ชิ้น, not sell-units). */
export function availablePieces(
  product: StockProduct,
  variant?: StockVariant | null,
): number {
  if (variant && !isSharedStock(product)) return Number(variant.stock ?? 0)
  return Number(product.stock ?? 0)
}

/**
 * Pieces consumed by ONE sold unit of this line — what OrderItem.unitCount is
 * snapshotted to, so every later restore/count uses the same number.
 *
 * Separate mode keeps the historic rule (variant.unitCount ?? product.unitCount)
 * untouched: shops there entered their stock in whatever unit they were already
 * selling, and re-reading pack sizes off names would silently multiply their
 * deductions. Shared mode is new, so it can use the accurate piece count.
 */
export function piecesPerUnit(product: StockProduct, variant?: StockVariant | null): number {
  if (isSharedStock(product)) return resolvePackSize(product, variant)
  if (variant) return Math.max(1, Number(variant.unitCount ?? 1))
  return Math.max(1, Number(product.unitCount ?? 1))
}

/**
 * How many of this option can still be sold — pieces on hand ÷ pieces per unit.
 * In รวม mode every option divides the SAME pool, so a 12-jar pool is 12 of the
 * "1 กระปุก" option or 4 of the "3 กระปุก" one; the AI is told both and the
 * server clamp uses the same arithmetic.
 */
export function sellableUnitsFor(product: StockProduct, variant?: StockVariant | null): number {
  return Math.floor(availablePieces(product, variant) / Math.max(1, piecesPerUnit(product, variant)))
}
